import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Wallet, Crown, Copy, Activity, Zap, TrendingUp, ArrowUpRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { fmtUsd, fmtPct } from "@/lib/format";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-md border border-[#262626] bg-[#141414] p-5 hover:border-[#333333] transition-colors" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.14em] text-[#737373] font-medium">{label}</span>
      <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.75} />
    </div>
    <div className="font-heading text-3xl font-bold text-[#F5F5F5] mt-3 tabular">{value}</div>
    {sub && <div className="text-xs text-[#737373] mt-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: stats, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard/stats")).data });
  const { data: summary } = useQuery({ queryKey: ["pf-summary"], queryFn: async () => (await api.get("/portfolio/summary")).data });

  const m = stats?.metrics || {};

  return (
    <div>
      <PageHeader
        title="Command Center"
        subtitle="Operational overview of your connected exchanges and copy-trading accounts."
        pageKey="dashboard"
        testid="dashboard-header"
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Wallet} label="Connected Exchanges" value={m.connected_exchanges ?? 0} sub="Distinct venues linked" testid="stat-exchanges" />
            <StatCard icon={Crown} label="Master Accounts" value={m.master_accounts ?? 0} sub="Source of truth" testid="stat-masters" />
            <StatCard icon={Copy} label="Connected Accounts" value={m.copy_accounts ?? 0} sub="Connected to a master" testid="stat-copies" />
            <StatCard icon={Activity} label="Active Accounts" value={m.active_accounts ?? 0} sub="Currently trading" testid="stat-active" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
            <div className="lg:col-span-2 rounded-md border border-[#262626] bg-[#141414] p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-lg font-semibold text-[#F5F5F5]">Portfolio Snapshot</h3>
                <button onClick={() => navigate("/portfolio")} data-testid="dashboard-view-portfolio" className="text-xs text-[#D4AF37] hover:text-[#F0C74B] flex items-center gap-1 transition-colors">
                  View Tracker <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#737373]">Total Balance</div>
                  <div className="font-mono text-2xl font-semibold text-[#F5F5F5] mt-1">{fmtUsd(summary?.total_balance)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#737373]">Avg Return</div>
                  <div className="font-mono text-2xl font-semibold text-[#10B981] mt-1">{fmtPct(summary?.avg_return)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#737373]">Total AUM</div>
                  <div className="font-mono text-2xl font-semibold text-[#F5F5F5] mt-1">{fmtUsd(summary?.total_aum)}</div>
                </div>
              </div>
            </div>

            <div className="rounded-md border border-[#262626] bg-[#141414] p-6">
              <h3 className="font-heading text-lg font-semibold text-[#F5F5F5] mb-4">Roadmap</h3>
              <div className="space-y-3">
                {(stats?.coming_soon || []).map((c) => (
                  <div key={c} className="flex items-center gap-3 text-sm text-[#A3A3A3]">
                    <div className="w-8 h-8 rounded-sm bg-[#1A1A1A] border border-[#262626] flex items-center justify-center">
                      {c === "automation" ? <Zap className="w-4 h-4 text-[#D4AF37]" /> : <TrendingUp className="w-4 h-4 text-[#D4AF37]" />}
                    </div>
                    <span className="capitalize">{c.replace("_", " ")}</span>
                    <span className="ml-auto text-[10px] uppercase tracking-wide text-[#525252]">Soon</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
